import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  Activity,
  CheckCircle2,
  Edit,
  History,
  Plug,
  Plus,
  Send,
  Trash2,
  Webhook as WebhookIcon,
  XCircle,
} from 'lucide-react';
import type { WebhookListItem } from '@news-cms/shared';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { WebhookDeliveriesDialog } from '@/components/webhooks/webhook-deliveries-dialog';
import { WebhookEditDialog } from '@/components/webhooks/webhook-edit-dialog';
import { WebhookSecretDialog } from '@/components/webhooks/webhook-secret-dialog';
import { useSession } from '@/lib/auth';
import { hasPermission } from '@/lib/permissions';
import {
  maskUrlSecret,
  useDeleteWebhook,
  useTestWebhook,
  useUpdateWebhook,
  useWebhooks,
} from '@/lib/webhooks';

interface WebhooksSearch {
  show?: 'failed';
}

export const Route = createFileRoute('/_authenticated/webhooks/')({
  validateSearch: (search: Record<string, unknown>): WebhooksSearch => ({
    show: search.show === 'failed' ? 'failed' : undefined,
  }),
  component: WebhooksPage,
});

/**
 * Tight relative format for the per-row delivery indicator. Falls
 * back to absolute MM/DD HH:mm beyond a day where "5 days ago" loses
 * useful precision.
 */
function formatDeliveryRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60_000);
  if (min < 1) return 'เมื่อสักครู่';
  if (min < 60) return `${min} นาทีที่แล้ว`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} ชม. ที่แล้ว`;
  return new Date(iso).toLocaleString('th-TH', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function WebhooksPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user } = useSession();
  const canManage = hasPermission(user, 'webhook.manage');
  const canReadAudit = hasPermission(user, 'audit.read');
  const query = useWebhooks();
  const deleteMutation = useDeleteWebhook();
  const testMutation = useTestWebhook();
  const updateMutation = useUpdateWebhook();

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<WebhookListItem | undefined>();
  const [pendingDelete, setPendingDelete] = useState<WebhookListItem | null>(null);
  const [revealSecret, setRevealSecret] = useState<string | null>(null);
  const [deliveriesFor, setDeliveriesFor] = useState<WebhookListItem | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; ok: boolean; status: number } | null>(
    null,
  );

  const allItems = query.data ?? [];
  // "Failed" = last delivery returned non-2xx. Hooks that have never
  // fired (lastDeliveryOk === null) stay out of the failed bucket —
  // a brand-new hook isn't broken, just untested.
  const failedCount = allItems.filter((w) => w.lastDeliveryOk === false).length;
  const items =
    search.show === 'failed' ? allItems.filter((w) => w.lastDeliveryOk === false) : allItems;

  if (!canManage) {
    return <div className="p-8 text-center text-sm text-slate-500">ไม่มีสิทธิ์จัดการ webhook</div>;
  }

  const openCreate = () => {
    setEditing(undefined);
    setEditOpen(true);
  };
  const openEdit = (w: WebhookListItem) => {
    setEditing(w);
    setEditOpen(true);
  };
  const handleDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id, {
      onSuccess: () => setPendingDelete(null),
      onError: () => setPendingDelete(null),
    });
  };
  const handleTest = (w: WebhookListItem) => {
    testMutation.mutate(
      { id: w.id, event: 'post.published' },
      {
        onSuccess: (result) => {
          setTestResult({ id: w.id, ok: result.ok, status: result.status });
          setTimeout(() => setTestResult(null), 4000);
        },
      },
    );
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
              <WebhookIcon className="text-brand-600 h-6 w-6" />
              Webhooks
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {items.length > 0
                ? `${items.length} subscription`
                : 'ส่ง event ของระบบไปยัง URL ภายนอก'}
            </p>
          </div>
          <Button onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            สร้าง Webhook
          </Button>
        </div>

        {/* Failed-only quick filter — hidden when nothing has failed
            recently (clean state shouldn't surface follow-up nudges).
            Red tint reads as "needs attention". */}
        {failedCount > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {(
              [
                { value: undefined, label: 'ทั้งหมด', count: allItems.length },
                { value: 'failed', label: 'ล้มเหลวล่าสุด', count: failedCount },
              ] as const
            ).map((p) => {
              const active = search.show === p.value;
              const isFailed = p.value === 'failed';
              return (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => void navigate({ search: { show: p.value } })}
                  className={
                    active
                      ? isFailed
                        ? 'rounded-full bg-red-600 px-3 py-1 text-xs font-medium text-white'
                        : 'bg-brand-600 rounded-full px-3 py-1 text-xs font-medium text-white'
                      : 'rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'
                  }
                >
                  {p.label} {p.count}
                </button>
              );
            })}
          </div>
        )}

        {query.isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900">
            กำลังโหลด...
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white p-12 text-center dark:border-slate-800 dark:bg-slate-900">
            <Plug className="h-12 w-12 text-slate-300" />
            <p className="text-sm text-slate-500">
              {search.show === 'failed'
                ? 'ไม่มี webhook ที่ส่งล้มเหลว 🎉'
                : 'ยังไม่มี webhook subscription'}
            </p>
            {search.show !== 'failed' && (
              <>
                <p className="max-w-md text-xs text-slate-400">
                  สร้าง webhook เพื่อให้ระบบส่ง event (post.published / unpublished / deleted) ไปยัง
                  URL ภายนอก พร้อมลายเซ็น HMAC-SHA256
                </p>
                <Button size="sm" onClick={openCreate}>
                  <Plus className="mr-2 h-4 w-4" />
                  สร้าง webhook แรก
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-3 font-medium">URL / คำอธิบาย</th>
                  <th className="px-4 py-3 font-medium">Events</th>
                  <th className="px-4 py-3 font-medium">สถานะ</th>
                  <th className="w-32 px-4 py-3 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {items.map((w) => (
                  <tr key={w.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="break-all font-mono text-xs text-slate-900 dark:text-slate-100">
                          {maskUrlSecret(w.url)}
                        </span>
                        {Date.now() - new Date(w.createdAt).getTime() < 24 * 60 * 60 * 1000 && (
                          <span
                            className="inline-flex items-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                            title="สร้างใหม่ใน 24 ชม.ที่ผ่านมา"
                          >
                            ใหม่
                          </span>
                        )}
                      </div>
                      {w.description && (
                        <div className="mt-0.5 text-[11px] text-slate-500">{w.description}</div>
                      )}
                      {/* Last-delivery indicator — saves a click to the
                          history dialog for the "did this fire? did it
                          succeed?" question. Click opens the existing
                          deliveries dialog for the full attempt log. */}
                      {w.lastDeliveryAt && (
                        <button
                          type="button"
                          onClick={() => setDeliveriesFor(w)}
                          className={`mt-1 inline-flex items-center gap-1 text-[10px] hover:underline ${
                            w.lastDeliveryOk
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}
                          title="ดูประวัติส่งทั้งหมด"
                        >
                          {w.lastDeliveryOk ? (
                            <CheckCircle2 className="h-2.5 w-2.5" />
                          ) : (
                            <XCircle className="h-2.5 w-2.5" />
                          )}
                          ส่งล่าสุด {formatDeliveryRelative(w.lastDeliveryAt)}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {w.events.map((e) => (
                          <span
                            key={e}
                            className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                          >
                            {e}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {/* Click toggles isActive without opening the edit
                          dialog — common gesture is "pause this hook
                          temporarily while debugging the receiver".
                          Optimistic invalidation already covered by
                          useUpdateWebhook's onSuccess. */}
                      <button
                        type="button"
                        onClick={() =>
                          updateMutation.mutate({ id: w.id, input: { isActive: !w.isActive } })
                        }
                        disabled={updateMutation.isPending}
                        aria-pressed={w.isActive}
                        title={w.isActive ? 'คลิกเพื่อปิด' : 'คลิกเพื่อเปิด'}
                        className={
                          w.isActive
                            ? 'inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800 hover:bg-emerald-200 disabled:opacity-50 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50'
                            : 'inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-200 disabled:opacity-50 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                        }
                      >
                        {w.isActive ? (
                          <>
                            <CheckCircle2 className="h-3 w-3" />
                            เปิด
                          </>
                        ) : (
                          <>
                            <XCircle className="h-3 w-3" />
                            ปิด
                          </>
                        )}
                      </button>
                      {testResult?.id === w.id && (
                        <div
                          className={`mt-1 text-[10px] ${
                            testResult.ok ? 'text-emerald-600' : 'text-red-600'
                          }`}
                        >
                          test {testResult.ok ? 'OK' : 'FAIL'} ({testResult.status})
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleTest(w)}
                          disabled={testMutation.isPending}
                          aria-label="ส่ง test payload"
                          title="ส่ง test payload"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeliveriesFor(w)}
                          aria-label="ดูประวัติส่ง"
                          title="ดูประวัติส่ง"
                        >
                          <History className="h-4 w-4" />
                        </Button>
                        {/* Audit drill — same pattern as /posts + /users.
                            "Deliveries" history shows attempts to the
                            target URL; "audit" shows admin changes to
                            this hook (created / events updated / paused). */}
                        {canReadAudit && (
                          <Link
                            to="/audit"
                            search={{ resourceType: 'webhook', resourceId: w.id }}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                            title="ดู audit ของ webhook นี้"
                            aria-label="ดู audit"
                          >
                            <Activity className="h-4 w-4" />
                          </Link>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(w)}
                          aria-label="แก้ไข"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setPendingDelete(w)}
                          aria-label="ลบ"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <WebhookEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        editing={editing}
        onCreated={(secret) => setRevealSecret(secret)}
      />

      <WebhookSecretDialog
        open={revealSecret !== null}
        onOpenChange={(open) => {
          if (!open) setRevealSecret(null);
        }}
        secret={revealSecret ?? ''}
      />

      <WebhookDeliveriesDialog
        open={deliveriesFor !== null}
        onOpenChange={(open) => {
          if (!open) setDeliveriesFor(null);
        }}
        webhookId={deliveriesFor?.id ?? null}
        webhookUrl={deliveriesFor ? maskUrlSecret(deliveriesFor.url) : undefined}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title="ลบ webhook?"
        description={
          pendingDelete
            ? `จะลบ subscription ไปยัง "${maskUrlSecret(pendingDelete.url)}" — receiver จะไม่ได้รับ event อีก`
            : undefined
        }
        confirmLabel="ลบ"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
